// ---------------------------------------------------------------------------
// Test Mode — simulated clients (12) for Clean Code Culture SRL (CLE-250/253).
//
// Client ids are reused by projects, invoices and leads so the same company
// appears consistently across the app. The first 9 clients own the 9 active
// projects and their invoiced totals mirror the project contracts so the
// per-client invoices, per-project P&L and the Financials overview all tie out
// to ~500K EUR income (CLE-253). The remaining 3 are smaller / historical
// accounts that are fully paid.
//
// Per-client invoiced distribution (EUR), summing to 500K across 15 projects
// (CLE-255 added 6 new clients/projects and rebalanced the originals so the
// company-wide income still ties out to ~500K EUR):
//   existing  c1 45 · c2 45 · c3 32 · c4 30 · c5 20 · c6 18 · c7 12 · c8 10 · c9 6  = 218
//   new       c13 38 (RetuRO) · c14 32 (FunCurier) · c15 85 (DSU) · c16 75 (MAE)
//             · c17 22 (Digital Print) · c18 30 (Let's Go Travel)                 = 282
//   total = 500K EUR
//
// CLE-260: "Venituri Totale" on /financials is the cumulative sum of PAID income
// invoices across all projects. To land that figure at the ~500K EUR target,
// every billed client is fully collected EXCEPT FinTech (c2), which keeps a
// single ~15K EUR open invoice so the Invoices/Clients modules still show a
// realistic outstanding balance. Paid income then totals ~485K EUR.
// ---------------------------------------------------------------------------

import type { ClientSummary, ClientDetail } from '../lib/clientsApi';
import type { ResourcesTree, TreeFile, TreeFolder } from '../lib/projectsApi';
import { isoDate, isoDateTime, eur } from './company';
import { testCompanyTag } from './companies';

interface ClientSeed {
  id: string;
  name: string;
  shortName: string;
  cifCui: string;
  contactPerson: string;
  email: string;
  phone: string;
  city: string;
  // Financial totals in RON.
  totalInvoiced: number;
  totalPaid: number;
}

export const CLIENT_SEEDS: ClientSeed[] = [
  {
    id: 'c1',
    name: 'FashionRO SRL',
    shortName: 'FashionRO',
    cifCui: 'RO22456789',
    contactPerson: 'Ana Maria Pop',
    email: 'contact@fashionro.ro',
    phone: '+40 264 111 222',
    city: 'Cluj-Napoca',
    totalInvoiced: eur(45_000),
    totalPaid: eur(45_000),
  },
  {
    id: 'c2',
    name: 'FinTech Partners SA',
    shortName: 'FinTech',
    cifCui: 'RO31567890',
    contactPerson: 'George Vasilescu',
    email: 'office@fintechpartners.ro',
    phone: '+40 21 333 444',
    city: 'București',
    // Single open invoice (~15K EUR) kept unpaid so the Invoices/Clients
    // modules still show a realistic outstanding balance (CLE-260).
    totalInvoiced: eur(45_000),
    totalPaid: eur(30_000),
  },
  {
    id: 'c3',
    name: 'DataViz Corp SRL',
    shortName: 'DataViz',
    cifCui: 'RO40678901',
    contactPerson: 'Laura Munteanu',
    email: 'hello@dataviz.ro',
    phone: '+40 256 555 666',
    city: 'Timișoara',
    totalInvoiced: eur(32_000),
    totalPaid: eur(32_000),
  },
  {
    id: 'c4',
    name: 'Mega Industries SRL',
    shortName: 'Mega',
    cifCui: 'RO15789012',
    contactPerson: 'Ion Popescu',
    email: 'contact@megaindustries.ro',
    phone: '+40 264 777 888',
    city: 'Cluj-Napoca',
    totalInvoiced: eur(30_000),
    totalPaid: eur(30_000),
  },
  {
    id: 'c5',
    name: 'LogiTrans SA',
    shortName: 'LogiTrans',
    cifCui: 'RO28890123',
    contactPerson: 'Dan Ardelean',
    email: 'office@logitrans.ro',
    phone: '+40 259 999 000',
    city: 'Oradea',
    totalInvoiced: eur(20_000),
    totalPaid: eur(20_000),
  },
  {
    id: 'c6',
    name: 'PeopleFirst SRL',
    shortName: 'PeopleFirst',
    cifCui: 'RO33901234',
    contactPerson: 'Carmen Iliescu',
    email: 'hr@peoplefirst.ro',
    phone: '+40 21 222 333',
    city: 'București',
    totalInvoiced: eur(18_000),
    totalPaid: eur(18_000),
  },
  {
    id: 'c7',
    name: 'SmartFactory SRL',
    shortName: 'SmartFactory',
    cifCui: 'RO42012345',
    contactPerson: 'Paul Roman',
    email: 'contact@smartfactory.ro',
    phone: '+40 256 444 555',
    city: 'Timișoara',
    totalInvoiced: eur(12_000),
    totalPaid: eur(12_000),
  },
  {
    id: 'c8',
    name: 'GovTech România SA',
    shortName: 'GovTech',
    cifCui: 'RO12123456',
    contactPerson: 'Mircea Stănescu',
    email: 'office@govtech.ro',
    phone: '+40 21 666 777',
    city: 'București',
    totalInvoiced: eur(10_000),
    totalPaid: eur(10_000),
  },
  {
    id: 'c9',
    name: 'EduPlatform SRL',
    shortName: 'EduPlatform',
    cifCui: 'RO50234567',
    contactPerson: 'Simona Barbu',
    email: 'hello@eduplatform.ro',
    phone: '+40 264 888 999',
    city: 'Cluj-Napoca',
    totalInvoiced: eur(6_000),
    totalPaid: eur(6_000),
  },
  {
    id: 'c10',
    name: 'HealthSync SRL',
    shortName: 'HealthSync',
    cifCui: 'RO51345678',
    contactPerson: 'Adrian Lupu',
    email: 'contact@healthsync.ro',
    phone: '+40 21 111 999',
    city: 'București',
    // Historical account — fully paid, no active project.
    totalInvoiced: 0,
    totalPaid: 0,
  },
  {
    id: 'c11',
    name: 'RetailHub SRL',
    shortName: 'RetailHub',
    cifCui: 'RO52456789',
    contactPerson: 'Oana Dragomir',
    email: 'office@retailhub.ro',
    phone: '+40 264 222 111',
    city: 'Cluj-Napoca',
    // Historical account — fully paid, no active project.
    totalInvoiced: 0,
    totalPaid: 0,
  },
  {
    id: 'c12',
    name: 'AgroTech SA',
    shortName: 'AgroTech',
    cifCui: 'RO53567890',
    contactPerson: 'Florin Mocanu',
    email: 'contact@agrotech.ro',
    phone: '+40 257 333 222',
    city: 'Arad',
    // Historical account — fully paid, no active project.
    totalInvoiced: 0,
    totalPaid: 0,
  },
  // --- CLE-255: 6 new clients owning the 6 new projects -------------------
  {
    id: 'c13',
    name: 'RetuRO SGR S.A.',
    shortName: 'RetuRO',
    cifCui: 'RO45000123',
    contactPerson: 'Maria Ionescu',
    email: 'office@returosgr.ro',
    phone: '+40 21 410 220',
    city: 'București',
    totalInvoiced: eur(38_000),
    totalPaid: eur(38_000),
  },
  {
    id: 'c14',
    name: 'FunCurier SRL',
    shortName: 'FunCurier',
    cifCui: 'RO45234098',
    contactPerson: 'Robert Dumitru',
    email: 'contact@funcurier.ro',
    phone: '+40 21 450 330',
    city: 'București',
    totalInvoiced: eur(32_000),
    totalPaid: eur(32_000),
  },
  {
    id: 'c15',
    name: 'Departamentul pentru Situații de Urgență',
    shortName: 'DSU',
    cifCui: 'RO46345109',
    contactPerson: 'Col. Vasile Gheorghe',
    email: 'contact@dsu.gov.ro',
    phone: '+40 21 208 6150',
    city: 'București',
    totalInvoiced: eur(85_000),
    totalPaid: eur(85_000),
  },
  {
    id: 'c16',
    name: 'Ministerul Afacerilor Externe',
    shortName: 'MAE',
    cifCui: 'RO47456210',
    contactPerson: 'Daniela Stoica',
    email: 'digital@mae.gov.ro',
    phone: '+40 21 319 2100',
    city: 'București',
    totalInvoiced: eur(75_000),
    totalPaid: eur(75_000),
  },
  {
    id: 'c17',
    name: 'Digital Print SRL',
    shortName: 'Digital Print',
    cifCui: 'RO48567321',
    contactPerson: 'Cristian Moldovan',
    email: 'office@digitalprint.ro',
    phone: '+40 256 470 440',
    city: 'Timișoara',
    totalInvoiced: eur(22_000),
    totalPaid: eur(22_000),
  },
  {
    id: 'c18',
    name: "Let's Go Travel SRL",
    shortName: "Let's Go Travel",
    cifCui: 'RO49678432',
    contactPerson: 'Dan Popescu',
    email: 'hello@letsgotravel.ro',
    phone: '+40 264 510 550',
    city: 'Cluj-Napoca',
    totalInvoiced: eur(30_000),
    totalPaid: eur(30_000),
  },
];

const CREATED_AT = isoDateTime(-480, 10);
const UPDATED_AT = isoDateTime(-5, 11);

export function clientName(id: string): string {
  return CLIENT_SEEDS.find((c) => c.id === id)?.name ?? 'Client necunoscut';
}

function summary(seed: ClientSeed, projectCount: number, invoiceCount: number): ClientSummary {
  return {
    id: seed.id,
    name: seed.name,
    shortName: seed.shortName,
    cifCui: seed.cifCui,
    regCom: `J12/${100 + Number(seed.id.replace('c', ''))}/2019`,
    email: seed.email,
    phone: seed.phone,
    website: `https://${seed.shortName.toLowerCase()}.ro`,
    country: 'România',
    contactPerson: seed.contactPerson,
    avatarUrl: null,
    color: null,
    defaultProjectId: null,
    isActive: true,
    ...testCompanyTag(seed.id),
    industryId: null,
    industry: null,
    projectCount,
    invoiceCount,
    totalInvoiced: seed.totalInvoiced,
    totalPaid: seed.totalPaid,
    outstanding: Math.max(0, seed.totalInvoiced - seed.totalPaid),
    currency: 'RON',
    totalReceived: seed.totalPaid,
    totalReceivedCurrency: 'RON',
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  };
}

// projectCounts/invoiceCounts are injected by the dataset builder to stay
// consistent with the projects + invoices modules.
export function buildClientSummaries(
  projectCounts: Record<string, number>,
  invoiceCounts: Record<string, number>,
): ClientSummary[] {
  return CLIENT_SEEDS.map((s) => summary(s, projectCounts[s.id] ?? 0, invoiceCounts[s.id] ?? 0));
}

export function buildClientDetail(
  id: string,
  projects: ClientDetail['projects'],
): ClientDetail | null {
  const seed = CLIENT_SEEDS.find((c) => c.id === id);
  if (!seed) return null;
  const outstanding = Math.max(0, seed.totalInvoiced - seed.totalPaid);
  return {
    id: seed.id,
    name: seed.name,
    shortName: seed.shortName,
    cifCui: seed.cifCui,
    regCom: `J12/${100 + Number(seed.id.replace('c', ''))}/2019`,
    email: seed.email,
    phone: seed.phone,
    website: `https://${seed.shortName.toLowerCase()}.ro`,
    address: `Str. Exemplu nr. ${seed.id.replace('c', '')}, ${seed.city}`,
    billingAddress: `Str. Exemplu nr. ${seed.id.replace('c', '')}, ${seed.city}`,
    country: 'România',
    contactPerson: seed.contactPerson,
    contactRole: 'Contact principal',
    contactEmail: seed.email,
    contactPhone: seed.phone,
    notes: 'Client generat în Test Mode pentru demonstrație.',
    avatarUrl: null,
    color: null,
    defaultProjectId: null,
    members: [],
    isActive: true,
    ...testCompanyTag(seed.id),
    industryId: null,
    industry: null,
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
    contacts: [
      {
        id: `${id}-contact-1`,
        clientId: id,
        name: seed.contactPerson,
        role: 'Contact principal',
        email: seed.email,
        phone: seed.phone,
        isPrimary: true,
        createdAt: CREATED_AT,
        updatedAt: UPDATED_AT,
      },
    ],
    projects,
    resources: [],
    meetingNotes: [
      {
        id: `${id}-note-1`,
        clientId: id,
        title: `Kickoff cu ${seed.contactPerson}`,
        date: isoDate(-30),
        attendees: `${seed.contactPerson}, Andrei Pata`,
        content: 'Discuție inițială despre cerințe și plan de livrare.',
        summary: 'Aliniere pe scope și milestone-uri.',
        actionItems: 'Pregătire propunere tehnică.',
        source: 'manual',
        s3Key: null,
        s3Url: null,
        createdAt: isoDateTime(-30, 15),
        updatedAt: isoDateTime(-30, 15),
      },
    ],
    financials: {
      currency: 'RON',
      totalInvoiced: seed.totalInvoiced,
      totalPaid: seed.totalPaid,
      outstanding,
    },
    counts: {
      projects: projects.length,
      contacts: 1,
      resources: 0,
      meetingNotes: 1,
    },
  };
}

// --- Client resources tree (DMS) ------------------------------------------

function clientResourceFile(
  id: string,
  name: string,
  category: string,
  createdOffset: number,
  fileSize: number,
): TreeFile {
  return {
    type: 'file',
    id,
    name,
    kind: 'pdf',
    fileType: 'application/pdf',
    fileSize,
    createdAt: isoDateTime(createdOffset, 10),
    downloadUrl: null,
    category,
  };
}

export function buildClientResourcesTree(clientId: string): ResourcesTree {
  const seed = CLIENT_SEEDS.find((c) => c.id === clientId);
  if (!seed) {
    return {
      tree: { type: 'folder', path: '/', name: 'root', children: [], fileCount: 0 },
      files: [],
    };
  }

  const files: TreeFile[] = [
    clientResourceFile(
      `${clientId}-res-contract`,
      `Contract cadru — ${seed.shortName}.pdf`,
      'Contracte',
      -120,
      256_000,
    ),
    clientResourceFile(`${clientId}-res-nda`, `NDA — ${seed.shortName}.pdf`, 'NDA', -115, 128_000),
    clientResourceFile(
      `${clientId}-res-propunere`,
      `Propunere comercială — ${seed.shortName}.pdf`,
      'Propuneri',
      -100,
      312_000,
    ),
    clientResourceFile(
      `${clientId}-res-factura1`,
      `Factură ${seed.shortName} — luna trecută.pdf`,
      'Facturi',
      -30,
      96_000,
    ),
    clientResourceFile(
      `${clientId}-res-factura2`,
      `Factură ${seed.shortName} — luna curentă.pdf`,
      'Facturi',
      -5,
      102_400,
    ),
  ];

  const byCategory = new Map<string, TreeFile[]>();
  for (const f of files) {
    const list = byCategory.get(f.category) ?? [];
    list.push(f);
    byCategory.set(f.category, list);
  }
  const children: TreeFolder[] = [...byCategory.entries()].map(([cat, fs]) => ({
    type: 'folder' as const,
    path: `/${cat}`,
    name: cat,
    children: fs,
    fileCount: fs.length,
  }));
  const tree: TreeFolder = {
    type: 'folder',
    path: '/',
    name: 'root',
    children,
    fileCount: files.length,
  };
  return { tree, files };
}
